import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useEquinorFlag } from 'utils/featureFlags';
import {
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  type CreateOrgEventAutomationRulePayload,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  EQUINOR_GRANE_EVENT_REGION,
  EQUINOR_GRANE_SPILL_RULE_NAME,
  EQUINOR_LOGISTICS_RULE_NAME,
  EQUINOR_MARINE_RECOVERY_RULE_NAME,
  EQUINOR_SHORELINE_RULE_NAME,
} from '../NotificationsTab/equinorGraneSpillNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

const EQUINOR_SEEDED_RULES: Array<{
  name: string;
  type: string;
  thresholdDescription: string;
}> = [
  {
    name: EQUINOR_GRANE_SPILL_RULE_NAME,
    type: 'Oil Spill',
    thresholdDescription:
      'Uncontrolled hydrocarbon release reaching open water from an offshore facility',
  },
  {
    name: EQUINOR_SHORELINE_RULE_NAME,
    type: 'Shoreline Impact',
    thresholdDescription:
      'Oil forecast to reach or already ashore on coastline',
  },
  {
    name: EQUINOR_MARINE_RECOVERY_RULE_NAME,
    type: 'Marine Recovery',
    thresholdDescription:
      'Sea state moderated enough for surface recovery at sheltered locations',
  },
  {
    name: EQUINOR_LOGISTICS_RULE_NAME,
    type: 'Logistics',
    thresholdDescription:
      'Response resources and recovered oil require a clear landing and offload site',
  },
];

/**
 * Ensures the Equinor group has spill / shoreline / recovery / logistics rules
 * so resource checklists can be keyed by rule name.
 *
 * There is no live oil-spill feed to point at, so these are seeded with no
 * source and disabled — they exist for the checklist keying, not to fire.
 */
export function useEnsureEquinorAutomationRule() {
  const equinorEnabled = useEquinorFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: equinorEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!equinorEnabled || !groupGuid || !isSuccess || attempted.current)
      return;
    const existingNames = new Set(
      (rules ?? []).filter((rule) => !rule.archived).map((rule) => rule.name)
    );
    const missing = EQUINOR_SEEDED_RULES.filter(
      (seed) => !existingNames.has(seed.name)
    );
    if (missing.length === 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `equinor-automation-rules:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const notifyConfig = {
      notificationTypes: [NotificationType.IN_APP],
      userIds: userGuid ? [userGuid] : [],
      teamIds: [],
      priority: 'watch',
      titleTemplate: 'Threshold event: {eventName}',
      bodyTemplate: '{eventName} impacts {resourceCount} resources.',
    };

    const promise = Promise.all(
      missing.map((seed) => {
        const payload: CreateOrgEventAutomationRulePayload = {
          group_guid: groupGuid,
          name: seed.name,
          enabled: false,
          logic: 'all',
          source: undefined,
          conditions: [],
          event_template: {
            severity: 'High',
            type: seed.type,
            region: EQUINOR_GRANE_EVENT_REGION,
            thresholdDescription: seed.thresholdDescription,
          },
          notify_on_create: false,
          checklist_on_create: false,
          checklist_config: { items: [] },
          notify_config: notifyConfig,
          created_by_user_guid: userGuid ?? null,
        };
        return createRule.mutateAsync(payload);
      })
    )
      .catch((err) => {
        console.warn('[equinor] failed to ensure automation rules', err);
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [equinorEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
